import { useRef, useCallback, useEffect } from 'react';
import { type OperationVariables } from '@apollo/react-common';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- `@confluence/apollo-relay-interop` package is the only one where such imports are allowed
	useLazyQuery,
	type QueryLazyOptions,
} from '@apollo/react-hooks';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop';
import { toDocumentNode } from './toDocumentNode';

export function useLazyQueryPromiseInterop<TData, TVariables = OperationVariables>(
	...[query, params]: Parameters<typeof useLazyQuery<TData, TVariables>>
): (options?: QueryLazyOptions<TVariables>) => Promise<TData> {
	const resolversRef = useRef<{
		resolve(data: TData): void;
		reject(error: ApolloErrorInterop): void;
	}>();
	/**
	 * NOTE: Using `onCompleted` and `onError` callbacks does not work as expected due to Apollo bug which is present in both v2 and v3 - https://github.com/apollographql/apollo-client/issues/9338
	 */
	const [doQuery, { data, error }] = useLazyQuery<TData, TVariables>(
		...[toDocumentNode(query), params],
	);

	useEffect(() => {
		if (data) resolversRef.current?.resolve?.(data);
		if (error) resolversRef.current?.reject?.(error);
	}, [data, error]);

	return useCallback(
		(vars?: QueryLazyOptions<TVariables>) => {
			let resolve: (data: TData) => void;
			let reject: (error: ApolloErrorInterop) => void;
			const promise = new Promise<TData>((res, rej) => {
				resolve = res;
				reject = rej;
			});

			// @ts-expect-error `resolve` and `reject` *will* be assigned via the Promise constructor, but TS can't guarantee that. This should be replaced with `Promise.withResolvers` once out jest setup properly supports that
			resolversRef.current = { resolve, reject };

			doQuery(vars);

			return promise;
		},
		[doQuery],
	);
}
